import {useCallback, useEffect, useRef, useState} from "react";
import {FormRepository, Guide, Question, Section} from "../../domain";
import {GroupedQuestions, groupQuestionsByAttributes,} from "../../core";
import {getFormRepository, getFormService, getScoreCalculator,} from "infrastructure/di/container";

// Declarar tipos para nuestra caché global
declare global {
  interface Window {
    __formDataCache?: {
      [key: string]: {
        cachedAnswers: Record<string, any>;
        cachedScores: Record<string, number>;
      };
    };
    __formDataLoading?: {
      [key: string]: boolean;
    };
  }
}

// Obtener servicios y repositorios del contenedor de dependencias
const formService = getFormService();
const formRepository = getFormRepository() as FormRepository;
const scoreCalculator = getScoreCalculator();

interface SectionProgress {
  id: string;
  title: string;
  total: number;
  answered: number;
  progress: number;
}

interface GuideWithDependents
  extends Omit<Guide, "serviceToClients" | "isBoss"> {
  serviceToClients?: {
    question: string;
    type: string;
    dependentQuestions: Question[];
  };
  isBoss?: {
    question: string;
    type: string;
    dependentQuestions: Question[];
  };
}

// Usamos la función groupQuestionsByAttributes del core en lugar de definirla aquí

/**
 * Hook personalizado para manejar el formulario
 * @param guiaKey Clave principal de la guía (NOM035)
 * @param formKey Clave de la guía específica (GUIA_I, GUIA_II, GUIA_III)
 */
interface UseFormOptions {
  initialAnswers?: Record<string, any>;
  initialScores?: Record<string, number>;
}

const useForm = (
  guiaKey: string,
  formKey: string,
  options: UseFormOptions = {}
) => {
  const { initialAnswers = {}, initialScores = {} } = options;
  const firstLoadRef = useRef(new Set<string>());


  const isBossKey = `${formKey}_isBoss`;
  const serviceKey = `${formKey}_serviceToClients`;


  const [answers, setAnswers] = useState<{ [key: string]: any }>(
    initialAnswers
  );
  const [scores, setScores] = useState<{ [key: string]: number }>(
    initialScores
  );

  // El resto de los estados se inicializan como antes.
  const [sections, setSections] = useState<Section[]>([]);
  const [groupedQuestions, setGroupedQuestions] = useState<GroupedQuestions>(
    {}
  );
  const [sectionProgress, setSectionProgress] = useState<SectionProgress[]>([]);
  const [activeSection, setActiveSection] = useState<string>("");
  const [scale, setScale] = useState<{ [key: string]: number } | undefined>(
    undefined
  );
  const [, setIsLoading] = useState(true);
  const [currentGuide, setCurrentGuide] = useState<GuideWithDependents | null>(
    null
  );

  const [showConnectionError, setShowConnectionError] = useState(false);


  // Esta función se encarga de cargar la ESTRUCTURA del formulario (preguntas, secciones),
  useEffect(() => {
    if (firstLoadRef.current.has(formKey)) return;
    firstLoadRef.current.add(formKey);

    const loadFormStructure = async () => {
      try {
        setIsLoading(true);
        const guide = await formRepository.getGuideByKey(formKey);

        if (guide) {
          setCurrentGuide(guide as GuideWithDependents);

          if (guide.sections) {
            setSections(guide.sections || []);
            setGroupedQuestions({});
            if (guide.sections.length > 0) {
              setActiveSection(guide.sections[0].title);
            }
          } else if (guide.questions) {
            const grouped = groupQuestionsByAttributes(guide.questions);
            setGroupedQuestions(grouped);
            setSections([]);
            const firstCategory = Object.keys(grouped)[0];
            if (firstCategory) {
              setActiveSection(firstCategory);
            }
          }

          if (guide.scale) {
            setScale(guide.scale);
          }
        }

        // luego de cargar la guia inicial, se cargan las demas, apiForm cuenta con un candado para no cargar las ya existentes
        await formRepository.init();
        setShowConnectionError(false);
      } catch (error) {
        console.error(
          `Error cargando la estructura del formulario para ${formKey}:`,
          error
        );
        setShowConnectionError(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadFormStructure();
  }, [guiaKey, formKey]); // Las dependencias ahora son mínimas y estables.

  /**
   * Maneja la respuesta a una pregunta
   * @param questionId ID de la pregunta
   * @param value Valor de la respuesta
   */
  const handleAnswer = async (
    questionId: string,
    value: any,
  ) => {
    try {
      console.log(`Setting answer for ${questionId}:`, value);

      // Actualizar estado local primero para una respuesta más rápida
      setAnswers((prevAnswers) => ({
        ...prevAnswers,
        [questionId]: value,
      }));

      // Guardar respuesta en segundo plano
      await formService
        .saveAnswer(guiaKey, formKey, questionId, value)
        .catch(console.error);

      // Obtener la guía actual para acceder a la escala
      const guide = await formRepository.getGuideByKey(formKey);
      if (!guide) return;

      // Obtener las preguntas
      const allQuestions = await formService.getAllQuestions(formKey);

      // resetear preguntas dependientes si el usuario se arrepintio
      if ((questionId === serviceKey || questionId === isBossKey) && value === false) {
        await formService.resetDependentQuestions(guiaKey, formKey, questionId, value);
        const [resetAnswers, resetScores] = await Promise.all([
          formRepository.getAnswers(guiaKey, formKey),
          formRepository.getScores(guiaKey, formKey)
        ]);
        setAnswers(resetAnswers ?? {});
        setScores(resetScores ?? {});
      }

      // Obtener las puntuaciones actuales
      const currentScores =
        (await formRepository.getScores(guiaKey, formKey)) || {};

      // Calcular la puntuación para la pregunta actual
      const question = allQuestions.find((q) => q.id === questionId);
      if (!question) return;

      // Calcular la puntuación usando el scoreCalculator
      const score = scoreCalculator.calculateScore(
        question,
        value,
        guide.scale
      );

      // Actualizar las puntuaciones
      const updatedScores = {
        ...currentScores,
        [questionId]: score,
      };

      // Actualizar el estado
      setScores(updatedScores);

      // Guardar las puntuaciones actualizadas
      await formRepository.saveScores(guiaKey, formKey, updatedScores);



    } catch (error) {
      console.error("Error al guardar la respuesta:", error);
    }
  };

  const applySystemAnswers = useCallback(
      async (
          updates: Record<string, any>,
          opts?: { base?: Record<string, any>; skipScoreIds?: string[] }
      ) => {
        const skipScore = new Set(opts?.skipScoreIds ?? []);

        // 1) UI optimista: merge local (respetando undefined = borrar)
        setAnswers(prev => {
          const merged = {...prev};
          for (const [id, v] of Object.entries(updates)) {
            if (v === undefined) delete merged[id];
            else merged[id] = v;
          }
          return merged;
        });

        // 2) Base desde tokenMap
        const repoBase = (await formRepository.getAnswers(guiaKey, formKey)) || {};

        // 3) Si nos pasan base autoritativa (p.ej. nextAns), que GANE sobre repo
        const base = opts?.base ? {...repoBase, ...opts.base} : repoBase;

        // 4) Merge final a persistir (undefined = borrar clave)
        const mergedForPersist: Record<string, any> = {...base};
        const touchedIds: string[] = [];
        for (const [id, v] of Object.entries(updates)) {
          touchedIds.push(id);
          if (v === undefined) delete mergedForPersist[id];
          else mergedForPersist[id] = v;
        }

        // 5) Persistir ANSWERS completos (no solo el parche)
        await formRepository.saveAnswers(guiaKey, formKey, mergedForPersist);

        // 6) Ajustar SCORES solo para ids tocados (y no skippeados)
        const currentScores = (await formRepository.getScores(guiaKey, formKey)) || {};
        const nextScores = {...currentScores};

        for (const id of touchedIds) {
          if (skipScore.has(id)) continue; // respeta el score calculado por FormService

          const v = updates[id];
          if (v === undefined) {
            delete nextScores[id];
          } else if (v === true) {
            nextScores[id] = 1; // en autoproceso booleano
          } else if (v === false) {
            nextScores[id] = 0;
          }
        }

        await formRepository.saveScores(guiaKey, formKey, nextScores);
        setScores(nextScores);
      },
      [guiaKey, formKey, setAnswers, setScores]
  );




  /**
   * Verifica si una sección debe ser visible según sus condiciones
   * @param section Sección a verificar
   */
  const isSectionVisible = (section: Section): boolean => {
    return formService.isSectionVisible(section, answers);
  };

  /**
   * Calcula las puntuaciones totales por categoría, dominio y dimensión
   */
  const calculateTotalScores = async () => {
    try {
      const allQuestions = await formService.getAllQuestions(formKey);

      const [categoryTotals, domainTotals, dimensionTotals] = await Promise.all(
        [
          scoreCalculator.calculateCategoryTotals(scores, allQuestions),
          scoreCalculator.calculateDomainTotals(scores, allQuestions),
          scoreCalculator.calculateDimensionTotals(scores, allQuestions),
        ]
      );

      return {
        categoryTotals,
        domainTotals,
        dimensionTotals,
      };
    } catch (error) {
      console.error("Error calculando puntuaciones totales:", error);
      return {
        categoryTotals: {},
        domainTotals: {},
        dimensionTotals: {},
      };
    }
  };

  // Calcular el progreso por secciones
  useEffect(() => {
    const calculateSectionProgress = () => {
      // Para secciones definidas explícitamente
      if (sections.length > 0) {
        const progress = sections
          .map((section) => ({
            id: section.title,
            title: section.title,
            total: section.questions?.length || 0,
            answered:
              section.questions?.filter(
                (q) => answers[q.id] !== undefined && answers[q.id] !== null
              ).length || 0,
            progress: 0,
          }))
          .map((section) => ({
            ...section,
            progress:
              section.total > 0 ? (section.answered / section.total) * 100 : 0,
          }));

        setSectionProgress(progress);
      }
      // Para preguntas agrupadas por categoría y dominio
      else if (Object.keys(groupedQuestions).length > 0) {
        const progress: SectionProgress[] = [];
        const specialSections: SectionProgress[] = [];

        if (currentGuide?.serviceToClients) {
          const dependentQuestions =
            currentGuide.serviceToClients.dependentQuestions || [];
          const total =
            1 +
            (answers[serviceKey] === true
              ? dependentQuestions.length
              : 0);
          const answered =
            (answers[serviceKey] !== undefined ? 1 : 0) +
            (answers[serviceKey] === true
              ? dependentQuestions.filter((q) => answers[q.id] !== undefined)
                  .length
              : 0);

          // Sección para serviceToClients si existe
          specialSections.push({
            id: "serviceToClients",
            title: "Servicio a Clientes",
            total,
            answered,
            progress: total > 0 ? (answered / total) * 100 : 0,
          });
        }

        // Sección para isBoss si existe
        if (currentGuide?.isBoss) {
          const dependentQuestions =
            currentGuide.isBoss.dependentQuestions || [];
          const total =
            1 + (answers[isBossKey] === true ? dependentQuestions.length : 0);
          const answered =
            (answers[isBossKey] !== undefined ? 1 : 0) +
            (answers[isBossKey] === true
              ? dependentQuestions.filter((q) => answers[q.id] !== undefined)
                  .length
              : 0);

          specialSections.push({
            id: "isBoss",
            title: "Jefatura",
            total,
            answered,
            progress: total > 0 ? (answered / total) * 100 : 0,
          });
        }

        //se buscan la preguntas dependientes y se agregan al set de escluidas
        const excludedQuestionIds = new Set<string>();
        if (currentGuide?.serviceToClients?.dependentQuestions) {
          currentGuide.serviceToClients.dependentQuestions.forEach((q) =>
            excludedQuestionIds.add(q.id)
          );
        }
        if (currentGuide?.isBoss?.dependentQuestions) {
          currentGuide.isBoss.dependentQuestions.forEach((q) =>
            excludedQuestionIds.add(q.id)
          );
        }

        // Procesar las categorías regulares
        Object.entries(groupedQuestions).forEach(([category, domains]) => {
          let totalQuestions = 0;
          let answeredQuestions = 0;

          Object.values(domains).forEach(
            (domain: { [dimension: string]: Question[] }) => {
              Object.values(domain).forEach((questions: Question[]) => {
                questions.forEach((question) => {
                  // se busca la pregunta dentro del las escluidad
                  // y si se encuentra no se agrega al conteo
                  if (excludedQuestionIds.has(question.id)) return;
                  totalQuestions++;
                  if (
                    answers[question.id] !== undefined &&
                    answers[question.id] !== null
                  ) {
                    answeredQuestions++;
                  }
                });
              });
            }
          );

          progress.push({
            id: category,
            title: category,
            total: totalQuestions,
            answered: answeredQuestions,
            progress:
              totalQuestions > 0
                ? (answeredQuestions / totalQuestions) * 100
                : 0,
          });
        });

        // Combinar secciones regulares y especiales
        let finalProgress = [...progress, ...specialSections];
        finalProgress = finalProgress.map((progress) => ({
          ...progress,
          title:
            progress.title.charAt(0).toUpperCase() +
            progress.title.slice(1).toLowerCase(),
        }));
        setSectionProgress(finalProgress);
      }
    };

    calculateSectionProgress();
  }, [sections, groupedQuestions, answers, currentGuide, isBossKey, serviceKey]);

  // Función para cambiar la sección activa
  const changeActiveSection = (sectionId: string) => {
    setActiveSection(sectionId);
  };

  return {
    sections,
    groupedQuestions,
    sectionProgress,
    activeSection,
    answers,
    scores,
    scale,
    showConnectionError,
    currentGuide,
    guides: { [formKey]: currentGuide }, // Agregar guías al objeto de retorno
    handleAnswer,
    isSectionVisible,
    calculateTotalScores,
    changeActiveSection,
    // Exponer setters para permitir la actualización externa del estado
    setAnswers,
    setScores,
    applySystemAnswers,
  };
};

export default useForm;
